import React from 'react';
import {
  FiX,
  FiSearch,
} from 'react-icons/fi';
import useSidebar from './useSidebar';
import { studentNavigationItems, navigationItems } from './sidebarNavigationItems';
import { SidebarContainer, SidebarHeader, SearchSection, SearchContainer, SearchInput, SearchIcon, SearchResults, SearchResultItem, SearchResultTitle, SearchResultSubtitle, NoResults, NavSection, SectionTitle, NavList, NavItem, NavLinkStyled, MobileOverlay, CloseButton, QuickStats, StatItem } from './Sidebar.styles';

function Sidebar({ isOpen, onClose }) {
  const {
    user,
    stats,
    searchTerm,
    searchResults,
    showSearchResults, setShowSearchResults,
    isSearching,
    handleSearchChange,
    handleSearchResultClick,
    handleSearchBlur,
  } = useSidebar(onClose);

  return (
    <>
      <MobileOverlay
        isOpen={isOpen}
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.3 }}
      />

      <SidebarContainer
        isOpen={isOpen}
        initial={{ x: -280 }}
        animate={{ x: 0 }}
        transition={{ duration: 0.3 }}
      >
        <CloseButton onClick={onClose}>
          <FiX size={20} />
        </CloseButton>

        <SidebarHeader>
          <h3>Navigation</h3>
          <p>Access your learning tools</p>
        </SidebarHeader>

        <SearchSection>
          <SearchContainer>
            <SearchIcon>
              <FiSearch size={16} />
            </SearchIcon>
            <SearchInput
              type="text"
              placeholder="Search the portal..."
              value={searchTerm}
              onChange={handleSearchChange}
              onFocus={() => setShowSearchResults(searchResults.length > 0)}
              onBlur={handleSearchBlur}
            />
            <SearchResults $show={showSearchResults}>
              {isSearching ? (
                <NoResults>Searching...</NoResults>
              ) : searchResults.length > 0 ? (
                searchResults.map((result) => (
                  <SearchResultItem
                    key={`${result.type}-${result.id}`}
                    onClick={() => handleSearchResultClick(result)}
                  >
                    <SearchResultTitle>{result.title}</SearchResultTitle>
                    <SearchResultSubtitle>{result.subtitle}</SearchResultSubtitle>
                  </SearchResultItem>
                ))
              ) : searchTerm ? (
                <NoResults>No results found</NoResults>
              ) : null}
            </SearchResults>
          </SearchContainer>
        </SearchSection>

        {(user?.role === 'student' ? studentNavigationItems : navigationItems)
          .map((section, index) => (
            <NavSection key={index}>
              <SectionTitle>{section.section}</SectionTitle>
              <NavList>
                {section.items
                  .filter(item => {
                    // Show item if no role restrictions
                    if (!item.adminOnly && !item.instructorOnly && !item.studentOnly) {
                      return true;
                    }
                    // Show admin-only items only to admins
                    if (item.adminOnly && user?.role === 'admin') {
                      return true;
                    }
                    // Show instructor-only items to instructors and admins
                    if (item.instructorOnly && (user?.role === 'instructor' || user?.role === 'admin')) {
                      return true;
                    }
                    // Show student-only items only to students
                    if (item.studentOnly && user?.role === 'student') {
                      return true;
                    }
                    return false;
                  })
                  .map((item) => {
                    const Icon = item.icon;
                    // Show "Courses" for instructors and admins, "My Courses" for students
                    const displayLabel = (item.path === '/courses' && (user?.role === 'instructor' || user?.role === 'admin'))
                      ? 'Courses'
                      : item.label;
                    return (
                      <NavItem key={item.path}>
                        <NavLinkStyled
                          to={item.path}
                          onClick={onClose}
                          end={item.path === '/admin'} // Only match exact path for admin dashboard
                        >
                          <Icon />
                          {displayLabel}
                        </NavLinkStyled>
                      </NavItem>
                    );
                  })}
              </NavList>
            </NavSection>
          ))}

        {user?.role === 'student' && (
          <QuickStats>
            <StatItem>
              <span className="label">Active Courses</span>
              <span className="value">{stats.courses}</span>
            </StatItem>
            <StatItem>
              <span className="label">Pending Tasks</span>
              <span className="value">{stats.pending}</span>
            </StatItem>
            <StatItem>
              <span className="label">Completed Tasks</span>
              <span className="value">{stats.completed}</span>
            </StatItem>
          </QuickStats>
        )}
      </SidebarContainer>
    </>
  );
}

export default Sidebar;
